//__OMISE_JS

export const createOmiseToken = (publicKey: string, cardData: any): Promise<{ id: string }> => {
  return new Promise((resolve, reject) => {
    if (typeof window === 'undefined' || !window.Omise) {
      reject(new Error('Omise.js not loaded'))
      return
    }
    window.Omise.setPublicKey(publicKey)
    window.Omise.createToken('card', cardData, (statusCode: number, response: any) => {
      if (statusCode !== 200) {
        reject(new Error(response.message || 'Failed to create Omise token'))
      } else {
        resolve({ id: response.id })
      }
    })
  })
}

interface EGiftFormData {
  formData: {
    name: string
    email: string
    phone: string
    [key: string]: any
  }
  orderItems: Array<{ id: string; quantity: number; isGift?: boolean }>
  promotionId: string | number
  csrf: string
  language: string
}

interface PaymentLinkFormData {
  formData: {
    name: string
    email: string
    phone: string
    pMethod: string
    cardToken?: string
    [key: string]: any
  }
  amount: number
  csrf: string
  language: string
  reference?: string
}

export const formatEGiftFormData = ({
  formData,
  orderItems,
  promotionId,
  csrf,
  language,
  totalPrice,
  currency,
  utmSource
}: EGiftFormData & { totalPrice?: number; currency?: string; utmSource?: string }) => {
  return [
    { name: 'csrf', value: csrf },
    { name: 'current_language', value: language },
    { name: 'order_type', value: 'eGift' },
    { name: 'promotion', value: promotionId.toString() },
    ...(typeof totalPrice !== 'undefined'
      ? [{ name: 'totalPrice', value: totalPrice.toString() }]
      : []),
    ...(currency ? [{ name: 'currency', value: currency }] : []),
    ...(utmSource ? [{ name: 'utm_source', value: utmSource }] : []),
    ...orderItems
      .map((item: { id: string; quantity: number; isGift?: boolean }, idx: number) => [
        { name: `items[${idx}][pd_id]`, value: item.id },
        { name: `items[${idx}][qty]`, value: item.quantity.toString() },
        ...(item.isGift ? [{ name: `items[${idx}][is_gift]`, value: '1' }] : [])
      ])
      .flat(),
    { name: 'name', value: formData.name },
    { name: 'email', value: formData.email },
    { name: 'dialCode', value: formData.phone.replace('+', '').slice(0, 2) },
    { name: 'phone', value: formData.phone },
    ...(formData.cardToken ? [{ name: 'cardToken', value: formData.cardToken }] : [])
  ]
}

export const formatPaymentLinkFormData = ({
  reference,
  formData,
  csrf,
  language,
  amount
}: PaymentLinkFormData) => {
  return [
    { name: 'order_type', value: 'paymentLink' },
    { name: 'reference', value: reference || '' },
    { name: 'language', value: language },
    { name: 'name', value: formData.name },
    { name: 'email', value: formData.email },
    { name: 'phone', value: formData.phone },
    { name: 'pMethod', value: formData.pMethod },
    { name: 'csrf', value: csrf },
    { name: 'amount', value: amount.toString() },
    ...(formData.cardToken ? [{ name: 'cardToken', value: formData.cardToken }] : [])
  ]
}

export const submitPaymentForm = (fields: Array<{ name: string; value: string }>) => {
  try {
    // Create a hidden form element
    const form = document.createElement('form')
    form.method = 'POST'
    form.action = '/payment/'
    form.style.display = 'none'

    // Add each field as a hidden input
    fields.forEach((field) => {
      const input = document.createElement('input')
      input.type = 'hidden'
      input.name = field.name
      input.value = field.value.toString()
      form.appendChild(input)
    })

    // Append form to body and submit
    document.body.appendChild(form)
    form.submit()

    // Clean up - remove form from DOM
    document.body.removeChild(form)
  } catch (error) {
    console.error('Form submission error:', error)
    throw new Error('Payment submission failed. Please try again.')
  }
}
